import { getValidToken } from 'src/auth/auth';
import { CONFIG_GENERATOR_ENDPOINT } from 'src/constants';
import {
  CheckProcedureRequest,
  CheckProcedureResponse,
  ColumnsRequest,
  ColumnsResponse,
  ConfigTestRequest,
  ConfigTestResponse,
  ConnectResponse,
  DdlParseRequest,
  GenerateDescriptionRequest,
  InferJobStatusResponse,
  InferJobSubmitResponse,
  InferMetricsRequest,
  InferMetricsResponse,
  InferParametersRequest,
  InferParametersResponse,
  InstallProceduresRequest,
  InstallProceduresResponse,
  RedshiftConnectionRequest,
  TablesRequest,
  TablesResponse,
  WriteCatalogsRequest,
  WriteCatalogsResponse,
} from './types';

const ENDPOINTS = {
  PARSE_DDL: '/api/onboarding/parse-ddl',
  CONNECT: '/api/onboarding/connect',
  CHECK_PROCEDURE: '/api/onboarding/check-procedure',
  INSTALL_PROCEDURES: '/api/onboarding/install-procedures',
  WRITE_CATALOGS: '/api/onboarding/write-catalogs',
  TEST_CONFIG: '/api/onboarding/test-config',
  TABLES: '/api/onboarding/tables',
  COLUMNS: '/api/onboarding/columns',
  INFER_METRICS: '/api/onboarding/infer-metrics',
  INFER_PARAMETERS: '/api/onboarding/infer-parameters',
  INFER_METRICS_ASYNC: '/api/onboarding/infer-metrics-async',
  INFER_PARAMETERS_ASYNC: '/api/onboarding/infer-parameters-async',
  GENERATE_DESCRIPTION_ASYNC: '/api/onboarding/generate-description-async',
  INFER_JOBS: '/api/onboarding/infer-jobs',
} as const;

export class OnboardingApiError extends Error {
  readonly statusCode: number;
  readonly code: string;
  constructor(statusCode: number, message: string, code: string = 'API_ERROR') {
    super(message);
    this.name = 'OnboardingApiError';
    this.statusCode = statusCode;
    this.code = code;
  }
}

async function apiRequest<TResponse>(
  endpoint: string,
  body: unknown,
  baseUrl?: string,
  method: 'POST' | 'GET' = 'POST',
): Promise<TResponse> {
  const base = baseUrl ?? CONFIG_GENERATOR_ENDPOINT;
  const url = `${base}${endpoint}`;

  let response: Response;
  try {
    // getValidToken() is called on EVERY request (incl. each GET poll tick) so
    // the bearer token stays fresh across a 60–90s polling window.
    const token = await getValidToken();
    const init: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };
    // GET requests carry no body (a Request with a GET method + body throws).
    if (method !== 'GET') {
      init.body = JSON.stringify(body);
    }
    response = await fetch(url, init);
  } catch (networkError) {
    const message =
      networkError instanceof Error
        ? networkError.message
        : 'Network error — check your connection';
    throw new OnboardingApiError(0, message, 'NETWORK_ERROR');
  }

  if (!response.ok) {
    let detail: string;
    let code = 'API_ERROR';
    try {
      const errorBody = await response.json();
      const raw = errorBody.error ?? errorBody.detail ?? errorBody.message ?? response.statusText;
      // API Gateway / non-string error payloads (objects, arrays) would render
      // as "[object Object]"; stringify them so the real error is visible.
      detail = typeof raw === 'string' ? raw : JSON.stringify(raw);
      code = errorBody.code ?? code;
    } catch {
      detail = response.statusText;
    }
    throw new OnboardingApiError(response.status, detail, code);
  }

  return response.json() as Promise<TResponse>;
}

export async function parseDdl(
  req: DdlParseRequest,
  baseUrl?: string,
): Promise<ColumnsResponse> {
  return apiRequest<ColumnsResponse>(ENDPOINTS.PARSE_DDL, req, baseUrl);
}

export async function connect(
  req: RedshiftConnectionRequest,
  baseUrl?: string,
): Promise<ConnectResponse> {
  return apiRequest<ConnectResponse>(ENDPOINTS.CONNECT, req, baseUrl);
}

export async function checkProcedure(
  req: CheckProcedureRequest,
  baseUrl?: string,
): Promise<CheckProcedureResponse> {
  return apiRequest<CheckProcedureResponse>(ENDPOINTS.CHECK_PROCEDURE, req, baseUrl);
}

/**
 * Installs the Bluey stored procedures (and their log table) into the user's
 * public schema when the check reports them missing. Idempotent: the underlying
 * DDL is CREATE OR REPLACE / CREATE TABLE IF NOT EXISTS, so a retry is safe. The
 * request is already snake_case (like writeCatalogs), so it is passed through
 * unmapped. The response reports what was installed and re-verifies via the
 * checker so the caller can confirm without a second round-trip.
 */
export async function installProcedures(
  req: InstallProceduresRequest,
  baseUrl?: string,
): Promise<InstallProceduresResponse> {
  return apiRequest<InstallProceduresResponse>(ENDPOINTS.INSTALL_PROCEDURES, req, baseUrl);
}

/**
 * Creates and populates the two metric-catalog tables Bluey's stored procedure
 * resolves metrics against. Create-only: the backend refuses with HTTP 409
 * (`CATALOG_NOT_EMPTY`) if EITHER table already holds rows, having inserted
 * nothing, so an existing catalog is never overwritten.
 *
 * The request is already snake_case (it mirrors the backend field names, like
 * the other Spec-1 endpoints), so it is passed through unmapped.
 */
export async function writeCatalogs(
  req: WriteCatalogsRequest,
  baseUrl?: string,
): Promise<WriteCatalogsResponse> {
  return apiRequest<WriteCatalogsResponse>(ENDPOINTS.WRITE_CATALOGS, req, baseUrl);
}

/**
 * Validates the already-assembled YAML against the backend's Bluey-config schema
 * (Review step, advisory only — never gates Finish). Synchronous schema check,
 * not an LLM job: a validation failure returns HTTP 200 with `approved: false`
 * and field-level errors, so only network/other failures throw an
 * OnboardingApiError. The request is already snake_case (like writeCatalogs), so
 * it is passed through unmapped.
 */
export async function testConfig(
  req: ConfigTestRequest,
  baseUrl?: string,
): Promise<ConfigTestResponse> {
  return apiRequest<ConfigTestResponse>(ENDPOINTS.TEST_CONFIG, req, baseUrl);
}

export async function listTables(
  req: TablesRequest,
  baseUrl?: string,
): Promise<TablesResponse> {
  return apiRequest<TablesResponse>(ENDPOINTS.TABLES, req, baseUrl);
}

export async function listColumns(
  req: ColumnsRequest,
  baseUrl?: string,
): Promise<ColumnsResponse> {
  return apiRequest<ColumnsResponse>(ENDPOINTS.COLUMNS, req, baseUrl);
}

/**
 * Calls the LLM Metric Inference API (Spec 2). Maps the logical
 * InferMetricsRequest to a snake_case wire body keyed by the backend model's
 * field NAMES (tool_name, source_table.{schema_name, table_name}, ...), which
 * is how the Spec-1 endpoints (connect/tables/columns) already send their
 * bodies. The backend binds these via `populate_by_name=True`; using field
 * names (not the camelCase aliases) keeps this consistent with the rest of the
 * codebase and robust if the model ever drops its aliases. The response comes
 * back camelCase and is returned as-is.
 */
export async function inferMetrics(
  req: InferMetricsRequest,
  baseUrl?: string,
): Promise<InferMetricsResponse> {
  const body: Record<string, unknown> = {
    tool_name: req.toolName,
    category: req.category,
    source_table: {
      schema_name: req.sourceTable.schema,
      table_name: req.sourceTable.tableName,
      columns: req.sourceTable.columns.map((c) => ({
        column_name: c.columnName,
        data_type: c.dataType,
        is_nullable: c.isNullable,
        distinct_values: c.distinctValues,
        sample_values: c.sampleValues,
      })),
    },
  };
  if (req.businessContext !== undefined) {
    body.business_context = req.businessContext;
  }
  if (req.existingMetricsCatalog !== undefined) {
    body.existing_metrics_catalog = req.existingMetricsCatalog;
  }
  return apiRequest<InferMetricsResponse>(ENDPOINTS.INFER_METRICS, body, baseUrl);
}

/**
 * Calls the LLM Parameter Inference API (Spec 3). Maps the logical
 * InferParametersRequest to the snake_case wire body keyed by the backend
 * model's field NAMES (tool_name, source_table.{schema_name, table_name},
 * confirmed_metrics.{base_metrics, derived_metrics}, supports_period_comparison,
 * …), the same convention as inferMetrics(). The response comes back camelCase
 * (inputSchema/required/warnings) and is returned as-is.
 */
export async function inferParameters(
  req: InferParametersRequest,
  baseUrl?: string,
): Promise<InferParametersResponse> {
  const body: Record<string, unknown> = {
    tool_name: req.toolName,
    category: req.category,
    source_table: {
      schema_name: req.sourceTable.schema,
      table_name: req.sourceTable.tableName,
      columns: req.sourceTable.columns.map((c) => ({
        column_name: c.columnName,
        data_type: c.dataType,
        is_nullable: c.isNullable,
        distinct_values: c.distinctValues,
        sample_values: c.sampleValues,
      })),
    },
    confirmed_metrics: {
      base_metrics: req.confirmedMetrics.baseMetrics.map((m) => ({
        metric_key: m.metricKey,
        expr_sql: m.exprSql,
        agg: m.agg,
        filter_condition: m.filterCondition,
      })),
      derived_metrics: req.confirmedMetrics.derivedMetrics.map((m) => ({
        derived_metric_key: m.derivedMetricKey,
        base_metrics_csv: m.baseMetricsCsv,
        expr_sql: m.exprSql,
        description: m.description,
      })),
    },
    supports_period_comparison: req.supportsPeriodComparison,
  };
  if (req.businessContext !== undefined) {
    body.business_context = req.businessContext;
  }
  return apiRequest<InferParametersResponse>(ENDPOINTS.INFER_PARAMETERS, body, baseUrl);
}

// ─── Async LLM inference (Spec 3.5): submit → poll ──────────────────────────
//
// The sync /infer-metrics and /infer-parameters routes run Bedrock behind API
// Gateway's hard 29s integration timeout, which the verbose parameter response
// reliably blows (and metrics intermittently). The `-async` routes return a
// jobId in <1s; a background Lambda runs the inference with no ceiling and
// stores the result in S3; the frontend polls GET /infer-jobs/{jobId}. The
// submit bodies are byte-identical to the sync ones (same snake_case wire
// shape) — only the endpoint differs.

/**
 * Submits a metric-inference job (Spec 3.5 async). POSTs the SAME snake_case
 * body inferMetrics() builds, but to the `-async` endpoint, returning the jobId
 * the caller then polls via pollInferJob().
 */
export async function submitInferMetrics(
  req: InferMetricsRequest,
  baseUrl?: string,
): Promise<InferJobSubmitResponse> {
  const body: Record<string, unknown> = {
    tool_name: req.toolName,
    category: req.category,
    source_table: {
      schema_name: req.sourceTable.schema,
      table_name: req.sourceTable.tableName,
      columns: req.sourceTable.columns.map((c) => ({
        column_name: c.columnName,
        data_type: c.dataType,
        is_nullable: c.isNullable,
        distinct_values: c.distinctValues,
        sample_values: c.sampleValues,
      })),
    },
  };
  if (req.businessContext !== undefined) {
    body.business_context = req.businessContext;
  }
  if (req.existingMetricsCatalog !== undefined) {
    body.existing_metrics_catalog = req.existingMetricsCatalog;
  }
  return apiRequest<InferJobSubmitResponse>(ENDPOINTS.INFER_METRICS_ASYNC, body, baseUrl);
}

/**
 * Submits a parameter-inference job (Spec 3.5 async). POSTs the SAME snake_case
 * body inferParameters() builds, but to the `-async` endpoint.
 */
export async function submitInferParameters(
  req: InferParametersRequest,
  baseUrl?: string,
): Promise<InferJobSubmitResponse> {
  const body: Record<string, unknown> = {
    tool_name: req.toolName,
    category: req.category,
    source_table: {
      schema_name: req.sourceTable.schema,
      table_name: req.sourceTable.tableName,
      columns: req.sourceTable.columns.map((c) => ({
        column_name: c.columnName,
        data_type: c.dataType,
        is_nullable: c.isNullable,
        distinct_values: c.distinctValues,
        sample_values: c.sampleValues,
      })),
    },
    confirmed_metrics: {
      base_metrics: req.confirmedMetrics.baseMetrics.map((m) => ({
        metric_key: m.metricKey,
        expr_sql: m.exprSql,
        agg: m.agg,
        filter_condition: m.filterCondition,
      })),
      derived_metrics: req.confirmedMetrics.derivedMetrics.map((m) => ({
        derived_metric_key: m.derivedMetricKey,
        base_metrics_csv: m.baseMetricsCsv,
        expr_sql: m.exprSql,
        description: m.description,
      })),
    },
    supports_period_comparison: req.supportsPeriodComparison,
  };
  if (req.businessContext !== undefined) {
    body.business_context = req.businessContext;
  }
  return apiRequest<InferJobSubmitResponse>(ENDPOINTS.INFER_PARAMETERS_ASYNC, body, baseUrl);
}

/**
 * Submits a description-generation job (Spec 6 async). Maps the logical
 * GenerateDescriptionRequest to the backend model's snake_case field NAMES
 * (tool_name, category, business_context, confirmed_metrics.{base_metrics,
 * derived_metrics}, parameter_names, supports_period_comparison) — the same
 * convention as submitInferMetrics/submitInferParameters. The v1 wizard has no
 * UI for sibling_tools/forbidden_topics, so they are OMITTED (the backend
 * defaults them to []). There is no source_table/columns field. Returns the
 * jobId the caller then polls via pollInferJob().
 */
export async function submitGenerateDescription(
  req: GenerateDescriptionRequest,
  baseUrl?: string,
): Promise<InferJobSubmitResponse> {
  const body: Record<string, unknown> = {
    tool_name: req.toolName,
    category: req.category,
    confirmed_metrics: {
      base_metrics: req.confirmedMetrics.baseMetrics.map((m) => ({
        metric_key: m.metricKey,
        expr_sql: m.exprSql,
        agg: m.agg,
        filter_condition: m.filterCondition,
      })),
      derived_metrics: req.confirmedMetrics.derivedMetrics.map((m) => ({
        derived_metric_key: m.derivedMetricKey,
        base_metrics_csv: m.baseMetricsCsv,
        expr_sql: m.exprSql,
        description: m.description,
      })),
    },
    parameter_names: req.parameterNames,
    supports_period_comparison: req.supportsPeriodComparison,
  };
  if (req.businessContext !== undefined) {
    body.business_context = req.businessContext;
  }
  return apiRequest<InferJobSubmitResponse>(ENDPOINTS.GENERATE_DESCRIPTION_ASYNC, body, baseUrl);
}

/**
 * Polls a single inference job's status/result (Spec 3.5 async). One shared GET
 * route for both job types — the caller knows whether `result` is a metrics or
 * parameters shape from which submit it issued. Calls getValidToken() per
 * request (it routes through apiRequest), so the token stays fresh across the
 * whole poll. A missing job (first-tick 404) surfaces as an OnboardingApiError
 * which pollInferJob() tolerates.
 */
export async function getInferJob(
  jobId: string,
  baseUrl?: string,
): Promise<InferJobStatusResponse> {
  const endpoint = `${ENDPOINTS.INFER_JOBS}/${encodeURIComponent(jobId)}`;
  return apiRequest<InferJobStatusResponse>(endpoint, undefined, baseUrl, 'GET');
}
